// src/pages/admin/DepositMatchPanel.jsx
import { useMemo, useState } from "react";
import { SegmentedControl, usePersistedState } from "@common";
import { BankFeedsButton } from "./BankFeedsModal";
import PracticeMatchGroup from "./PracticeMatchGroup";
import { MATCH_GRID } from "./DepositMatchRow";
import { FILTERS, fmtShortDay, formatCad, formatSignedCad } from "./depositMatchUtils";

const cents = (n) => Math.round((Number(n) || 0) * 100);
const lineKey = (side, line) => `${side}|${line.id}`;

/**
 * DepositMatchPanel — deposit-sheet lines against bank transactions, one
 * collapsible group per practice.
 *
 *   practices: [{ id, name, bank, manager, rows, stats, bankNote }]
 *   bankRange: { from, to } the bank side was fetched for — it runs a few days
 *   past the filter bar's window, so the column header says how far.
 *
 * Dates come from the filter bar; this panel has no date controls of its own.
 *
 * Manual matching (when `canMatch`): tick any number of unmatched deposit and
 * bank lines within ONE practice, then "Match selected". The button only
 * enables when both sides total the same to the cent.
 *   onCreateMatch({ practiceId, deposits, banks }) → Promise
 *   onUnmatch(matchId) → Promise
 */
export default function DepositMatchPanel({
  practices = [],
  bankRange,
  // Opens the Bank feeds modal (every clinic's banks) from the To post tab.
  onOpenBankFeeds,
  canMatch = false,
  onCreateMatch,
  onUnmatch,
  matching = false,
  unmatchingId = null,
  matchError = null,
  // Suggestions: accept one ({ practiceId, row }) or every exact one at once.
  onAcceptSuggestion,
  onAcceptAll,
  acceptingId = null,
  acceptingAll = false,
}) {
  const [filter, setFilter] = usePersistedState("dailyDeposits:filter", "needs");
  const [collapsed, setCollapsed] = useState(() => new Set());
  // Map lineKey → { side, line, practiceId }
  const [picked, setPicked] = useState(() => new Map());

  // Drop picks that are no longer pending (matched elsewhere, data reloaded).
  const pendingKeys = useMemo(() => {
    const keys = new Set();
    for (const p of practices) {
      for (const r of p.rows) {
        if (r.status !== "depositPending" && r.status !== "bankPending") continue;
        r.deposits.forEach((d) => keys.add(lineKey("deposit", d)));
        r.banks.forEach((b) => keys.add(lineKey("bank", b)));
      }
    }
    return keys;
  }, [practices]);
  const selection = useMemo(
    () => [...picked.entries()].filter(([k]) => pendingKeys.has(k)).map(([, v]) => v),
    [picked, pendingKeys],
  );
  const selDeposits = selection.filter((x) => x.side === "deposit").map((x) => x.line);
  const selBanks = selection.filter((x) => x.side === "bank").map((x) => x.line);
  const depTotal = selDeposits.reduce((n, l) => n + l.amount, 0);
  const bankTotal = selBanks.reduce((n, l) => n + l.amount, 0);
  const diffCents = cents(bankTotal) - cents(depTotal);
  const canSubmit = selDeposits.length > 0 && selBanks.length > 0 && !matching;

  const isSelected = (side, line) => picked.has(lineKey(side, line));
  const toggleLine = (practiceId) => (side, line) =>
    setPicked((prev) => {
      // A match belongs to one clinic: picking in another practice starts over.
      const samePractice = [...prev.values()].every((x) => x.practiceId === practiceId);
      const next = samePractice ? new Map(prev) : new Map();
      const k = lineKey(side, line);
      if (next.has(k)) next.delete(k);
      else next.set(k, { side, line, practiceId });
      return next;
    });
  const clearSelection = () => setPicked(new Map());

  const submit = async () => {
    if (!canSubmit) return;
    try {
      await onCreateMatch?.({
        practiceId: selection[0].practiceId,
        deposits: selDeposits,
        banks: selBanks,
      });
      clearSelection();
    } catch {
      // The caller surfaces the error via `matchError`; keep the picks.
    }
  };

  const counts = useMemo(() => {
    const c = {
      matched: 0,
      suggested: 0,
      variance: 0,
      depositPending: 0,
      bankPending: 0,
      excluded: 0,
    };
    for (const p of practices) {
      for (const r of p.rows) c[r.status] += 1;
    }
    return c;
  }, [practices]);

  const options = [
    {
      value: "needs",
      label: `Needs matching · ${
        counts.suggested + counts.variance + counts.depositPending + counts.bankPending
      }`,
    },
    { value: "suggested", label: `Suggested · ${counts.suggested}` },
    { value: "depositPending", label: `Deposits pending · ${counts.depositPending}` },
    { value: "bankPending", label: `Bank pending · ${counts.bankPending}` },
    { value: "matched", label: `Matched · ${counts.matched}` },
    { value: "toPost", label: "To post" },
    { value: "all", label: "All" },
  ];

  // Practices with nothing in the current view are hidden, except under "All".
  const shown = useMemo(() => {
    const statuses = FILTERS[filter] ?? null;
    return practices
      .map((p) => ({
        ...p,
        visibleRows: statuses ? p.rows.filter((r) => statuses.includes(r.status)) : p.rows,
      }))
      .filter((p) => !statuses || p.visibleRows.length > 0);
  }, [practices, filter]);

  const exactSuggestions = useMemo(
    () =>
      practices.flatMap((p) =>
        p.rows
          .filter((r) => r.status === "suggested" && r.confidence === "exact")
          .map((row) => ({ practiceId: p.id, row })),
      ),
    [practices],
  );

  const allCollapsed = shown.length > 0 && shown.every((p) => collapsed.has(p.id));
  const toggle = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-palette-slate-divider bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5 border-b border-palette-slate-divider px-4 py-2.5">
          <div className="max-w-full overflow-x-auto">
            <SegmentedControl options={options} value={filter} onChange={setFilter} />
          </div>
          <span className="flex-1" />
          {filter === "toPost" && <BankFeedsButton onClick={onOpenBankFeeds} />}
          {canMatch && exactSuggestions.length > 0 && (
            <button
              type="button"
              onClick={() => onAcceptAll?.(exactSuggestions)}
              disabled={acceptingAll}
              title="Save every suggestion whose amount and memo both agree"
              className="h-[30px] whitespace-nowrap rounded-md bg-palette-indigo-badge px-3 text-[12.5px] font-semibold text-white transition-colors hover:bg-palette-indigo-heading disabled:opacity-50"
            >
              {acceptingAll ? "Accepting…" : `Accept all exact · ${exactSuggestions.length}`}
            </button>
          )}
          <button
            type="button"
            onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(shown.map((p) => p.id)))}
            className="h-[30px] whitespace-nowrap rounded-md px-2.5 text-[12.5px] font-semibold text-palette-emerald-hover transition-colors hover:bg-palette-emerald-soft"
          >
            {allCollapsed ? "Expand all" : "Collapse all"}
          </button>
        </div>

        <div
          className={`${MATCH_GRID} sticky top-0 z-20 border-b border-palette-slate-divider bg-palette-slate-soft px-4 py-2 text-[11px] font-semibold uppercase tracking-wider`}
        >
          <span className="col-span-2 text-palette-slate-placeholder">Deposits</span>
          <span className="col-span-2 text-right text-palette-emerald-hover">
            Bank{bankRange?.to ? ` · through ${fmtShortDay(bankRange.to)}` : ""}
          </span>
        </div>

        {shown.length === 0 ? (
          <p className="px-4 py-6 text-sm text-palette-slate-secondary">
            {filter === "toPost"
              ? "Nothing to post yet."
              : practices.length === 0
                ? "No Collection Breakdown or bank deposits in these dates."
                : "Nothing in this view."}
          </p>
        ) : (
          shown.map((p) => (
            <PracticeMatchGroup
              key={p.id}
              {...p}
              open={!collapsed.has(p.id)}
              onToggle={() => toggle(p.id)}
              canMatch={canMatch}
              isSelected={isSelected}
              onToggleLine={toggleLine(p.id)}
              onUnmatch={onUnmatch}
              unmatchingId={unmatchingId}
              onAccept={(row) => onAcceptSuggestion?.({ practiceId: p.id, row })}
              acceptingId={acceptingId}
            />
          ))
        )}
      </div>

      {canMatch && (selection.length > 0 || matchError) && (
        // Outside the card: its overflow-hidden would stop `sticky` working.
        <div className="sticky bottom-4 z-30 mx-auto flex w-full max-w-3xl flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-palette-indigo-border bg-white px-4 py-3 shadow-xl">
          <div className="flex min-w-0 flex-col text-[12.5px] tabular-nums">
            <span className="text-palette-slate-heading">
              <b>{selDeposits.length}</b> deposit {selDeposits.length === 1 ? "line" : "lines"} ·{" "}
              {formatCad(depTotal)}
              <span className="mx-2 text-palette-slate-placeholder">↔</span>
              <b>{selBanks.length}</b> bank {selBanks.length === 1 ? "line" : "lines"} ·{" "}
              {formatCad(bankTotal)}
            </span>
            {matchError ? (
              <span className="font-semibold text-palette-rose-heading">{matchError}</span>
            ) : selDeposits.length === 0 || selBanks.length === 0 ? (
              <span className="text-palette-slate-placeholder">
                Pick at least one line on each side.
              </span>
            ) : diffCents !== 0 ? (
              <span className="font-semibold text-palette-amber-primary">
                Off by {formatSignedCad(diffCents / 100)} — saved as a variance.
              </span>
            ) : (
              <span className="font-semibold text-palette-emerald-hover">Totals agree.</span>
            )}
          </div>
          <span className="flex-1" />
          <button
            type="button"
            onClick={clearSelection}
            className="h-9 rounded-lg px-3 text-[13px] font-semibold text-palette-slate-secondary transition-colors hover:bg-palette-slate-muted"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="h-9 rounded-lg bg-palette-indigo-badge px-4 text-[13px] font-semibold text-white transition-colors hover:bg-palette-indigo-heading disabled:cursor-not-allowed disabled:opacity-50"
          >
            {matching ? "Matching…" : "Match selected"}
          </button>
        </div>
      )}
    </>
  );
}
